import { Branch } from '../../../dorian-shared/types/branch/Branch';
import { BranchStep, StepTypes } from '../../../dorian-shared/types/branch/BranchStep';
import type { StepCheckSwitch } from '../Book/MemoryBank/memoryBankTypes';
import { HubOption, isHubGotoProp } from '../../../dorian-shared/types/branch/HubOption';
import type { Mod, HubGotoProp } from '../../../dorian-shared/types/branch/HubOption';

type GotoMod = Mod & { prop: HubGotoProp; val: { gotoBranchId: number } };

function asGotoMod(mod: unknown): GotoMod | null {
  if (!mod || typeof mod !== 'object') return null;
  const m = mod as Partial<Mod>;
  if (!isHubGotoProp(m.prop)) return null;
  const goto = (m.val as { gotoBranchId?: unknown } | undefined)?.gotoBranchId;
  if (goto == null) return null;
  return m as GotoMod;
}

export const BRANCH_LINES_TYPES = {
  Link: 'link',
  Branch: 'branch',
  SwitchTrue: 'switch_true',
  SwitchFalse: 'switch_false',
  HubOverride: 'hub_override',
  HubReplayOverride: 'hub_replay_override',
} as const;

export const BRANCH_LINES_CLASSES = {
  link: 'line_link',
  switchTrue: 'line_switch_true',
  switchFalse: 'line_switch_false',
  branch: 'line_branch',
  hubOverride: 'line_hub_override',
  hubReplayOverride: 'line_hub_replay_override',
} as const;

export type LineType = typeof BRANCH_LINES_TYPES[keyof typeof BRANCH_LINES_TYPES];

export function getClassNameByLineType(lineType: string): string {
  switch (lineType) {
    case BRANCH_LINES_TYPES.Link:
      return BRANCH_LINES_CLASSES.link;
    case BRANCH_LINES_TYPES.SwitchTrue:
      return BRANCH_LINES_CLASSES.switchTrue;
    case BRANCH_LINES_TYPES.SwitchFalse:
      return BRANCH_LINES_CLASSES.switchFalse;
    case BRANCH_LINES_TYPES.HubOverride:
      return BRANCH_LINES_CLASSES.hubOverride;
    case BRANCH_LINES_TYPES.HubReplayOverride:
      return BRANCH_LINES_CLASSES.hubReplayOverride;
    case BRANCH_LINES_TYPES.Branch:
    default:
      return BRANCH_LINES_CLASSES.branch;
  }
}

export type LineEdge = {
  fromID: number;
  toID: number;
  type: LineType;
  index: number;
};

function lineTypeForGotoProp(prop: HubGotoProp): LineType {
  return prop === 'replay.goto'
    ? BRANCH_LINES_TYPES.HubReplayOverride
    : BRANCH_LINES_TYPES.HubOverride;
}

/**
 * Collect all line edges for a single branch node.
 * Extracts goto links, switch links, hub option links, and hub override (mods) links.
 */
function collectHubOverrideEdges(
  fromID: number,
  hubOptions: HubOption[],
  existingToIds: Set<number>,
  startIdx: number,
): LineEdge[] {
  const edges: LineEdge[] = [];
  let idx = startIdx;
  hubOptions.forEach((opt) => {
    (opt.mods ?? []).forEach((mod) => {
      const gotoMod = asGotoMod(mod);
      if (!gotoMod) return;
      const modGoto = Number(gotoMod.val.gotoBranchId);
      if (!existingToIds.has(modGoto)) {
        edges.push({
          fromID,
          toID: modGoto,
          type: lineTypeForGotoProp(gotoMod.prop),
          index: idx,
        });
        existingToIds.add(modGoto);
        idx += 1;
      }
    });
  });
  return edges;
}

export function collectBranchEdges(object: Branch, branchIndex: number): LineEdge[] {
  const fromID = Number(object.id);
  const hubStep: BranchStep | undefined = object.steps?.find(
    (s) => Number(s.stepTypeId) === StepTypes.Hub,
  );
  const hubOptions: HubOption[] = hubStep?.hubOptions ?? [];

  // Hub nodes: derive edges exclusively from hubOptions/mods. We intentionally
  // ignore object.links here — after a save the backend delta response returns
  // links:[] even when destinations still exist, which made arrows disappear
  // until a full page reload (DOR-10517). hubOptions + mods are the single
  // source of truth for a hub node's destinations.
  if (hubOptions.length > 0) {
    const edges: LineEdge[] = [];
    const existing = new Set<number>();

    hubOptions.forEach((opt) => {
      if (opt.gotoBranchId != null) {
        const goto = Number(opt.gotoBranchId);
        if (!existing.has(goto)) {
          edges.push({
            fromID, toID: goto, type: BRANCH_LINES_TYPES.Link, index: edges.length,
          });
          existing.add(goto);
        }
      }
      const replayGoto = opt.replay?.gotoBranchId;
      if (replayGoto != null) {
        const goto = Number(replayGoto);
        if (!existing.has(goto)) {
          edges.push({
            fromID, toID: goto, type: BRANCH_LINES_TYPES.HubReplayOverride, index: edges.length,
          });
          existing.add(goto);
        }
      }
    });

    edges.push(...collectHubOverrideEdges(fromID, hubOptions, existing, edges.length));
    return edges;
  }

  // Non-hub nodes with links (e.g. choice answers aggregated by backend into object.links)
  if (object.links !== undefined && object.links.length > 0) {
    const links = object.links as unknown as number[];
    return links.map((link, j) => ({
      fromID,
      toID: Number(link),
      type: BRANCH_LINES_TYPES.Link,
      index: j,
    }));
  }

  // Switch branches
  if (object.switch && object.switch.length > 0) {
    return object.switch.map((sw: StepCheckSwitch, j) => {
      const switchValue = sw.value === 'true' || sw.value === true;
      return {
        fromID,
        toID: Number(sw.gotoBranchId),
        type: switchValue ? BRANCH_LINES_TYPES.SwitchTrue : BRANCH_LINES_TYPES.SwitchFalse,
        index: j,
      };
    });
  }

  // Simple goto
  if (object.gotoBranchId !== null) {
    return [{
      fromID,
      toID: Number(object.gotoBranchId),
      type: BRANCH_LINES_TYPES.Branch,
      index: branchIndex,
    }];
  }

  return [];
}

/**
 * Collect all goto node IDs for cascade selection (includes override destinations).
 */
export function collectCascadeIds(node: Branch): number[] {
  const gotoBranchId = node.gotoBranchId ? [Number(node.gotoBranchId)] : [];
  const gotoSwitch = node.switch?.map((el: StepCheckSwitch) => Number(el.gotoBranchId)) ?? [];

  const hubStep: BranchStep | undefined = node.steps?.find(
    (s) => Number(s.stepTypeId) === StepTypes.Hub,
  );
  const hubOptions: HubOption[] = hubStep?.hubOptions ?? [];

  // For hub nodes, hubOptions/mods are the single source of truth (see collectBranchEdges).
  // For non-hub nodes without a direct goto/switch, fall back to object.links for answers.
  const gotoLinks = hubOptions.length > 0
    ? []
    : ((node.links as unknown as number[] | undefined) ?? []).map((el) => Number(el));

  const gotoHub: number[] = hubOptions.flatMap((opt) => {
    const ids: number[] = [];
    if (opt.gotoBranchId) ids.push(Number(opt.gotoBranchId));
    if (opt.replay?.gotoBranchId) ids.push(Number(opt.replay.gotoBranchId));
    (opt.mods ?? []).forEach((mod) => {
      const gotoMod = asGotoMod(mod);
      if (gotoMod) {
        ids.push(Number(gotoMod.val.gotoBranchId));
      }
    });
    return ids;
  });

  return [...gotoBranchId, ...gotoLinks, ...gotoSwitch, ...gotoHub];
}
